import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { getToken, logout, setAuthData } from "../helpers/authHelper";
import { AuthErrorScreen } from "./auth/AuthShowcase";
import { SkeletonCenteredForm, SkeletonShell } from "./skeleton/Skeleton";

const ProtectedRoute = ({ requireOnboarding, requiredRole }) => {
  const location = useLocation();
  const token = getToken();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [authCheckError, setAuthCheckError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    setAuthCheckError("");
    // Role and onboarding status always come from the server's record of the account, never the cached copy.
    let active = true;
    apiRequest("/api/auth/me")
      .then(({ user: currentUser }) => {
        if (!active) return;
        setAuthData(token, currentUser);
        setUser(currentUser);
      }).catch((requestError) => {
        if (!active) return;
        // apiRequest has already restored the admin's own session; the impersonation banner takes them back.
        if (requestError.data?.code === "IMPERSONATION_ENDED") return;
        if ([401, 403].includes(requestError.status)) {
          logout();
          setUser(null);
          return;
        }
        setAuthCheckError(requestError.message || "Unable to verify your session. Check your connection and try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [retryCount, requiredRole, token]);

  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  if (loading) return requireOnboarding === false ? <SkeletonCenteredForm label="Loading your workspace" /> : <SkeletonShell />;
  if (authCheckError) return <AuthErrorScreen message={authCheckError} onRetry={() => { setLoading(true); setRetryCount((count) => count + 1); }} />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (requiredRole && user.role !== requiredRole) return <Navigate to="/dashboard" replace />;
  if (user.role === "admin" && requireOnboarding !== undefined) return <Navigate to="/admin/dashboard" replace />;

  if (user.role !== "admin" && requireOnboarding && !user.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }
  if (user.role !== "admin" && requireOnboarding === false && user.onboardingComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
